import { memo, useEffect, useRef, useState, VFC } from 'react'
import {
  ContibutionsData,
  fetchContributions,
  fetchContributionSearchConditions,
  fetchMyContributions,
} from 'src/api/contribution'
import { useMasterData } from 'src/api/masterData'
import { ExpandLessRounded, ExpandMoreRounded } from 'src/assets/svgs'
import { MemberHeader, MemberHeaderProps } from 'src/components/Header'
import { TipsLink } from 'src/components/Link'
import { Pagination } from 'src/components/Pagination'
import { ProfileViewModal } from 'src/components/ProfilePreview'
import {
  Contribution,
  CONTRIBUTION_SEARCH_CONDITIONS_MAX_COUNT,
  ContributionKeywordsParam,
  ContributionSearchConditionsData,
  ContributionsQueryParam,
} from 'src/models/Contribution'
import { MasterData } from 'src/models/MasterData'
import { EMPTY_LOGIN_USER } from 'src/models/User'
import { fontWeightBold } from 'src/styles/font'
import { companyDetail, GUIDE_LINK } from 'src/utils/routes'
import styled, { css } from 'styled-components'
import {
  _contributionsBackground,
  _contributiontext,
  _filterbg,
  _profilegreen,
  defaultShadow,
  truewhite,
} from '../../styles/colors'
import { ContributionCard } from './ContributionCard'
import { ContributionMessagesModal } from './ContributionMessagesModal'
import { Filter } from './Filter'
import { FilterCreatePopUpMenu } from './FilterCreatePopUpMenu'
import { FilterDeleteModal } from './FilterDeleteModal'
import { FilterSettingModal } from './FilterSettingModal'
import { FilterSettingParams } from './FilterSettingParams'
import { SearchFilter } from './SearchFilter'
import { SIDE_ICON_MENU_WIDTH, SideIconMenu } from './SideIconMenu'

type ContributionsLayoutProps = MemberHeaderProps &
  Omit<ContributionContentProps, 'userId' | 'showProfile' | 'showMessages'>

export const ContributionsLayout: VFC<ContributionsLayoutProps> = ({
  user,
  ...props
}) => {
  const [viewingUserId, setViewingUserId] = useState<number | undefined>()
  const [viewingContributionId, setViewingContributionId] = useState<
    number | undefined
  >()

  if (user.id === EMPTY_LOGIN_USER.id) return <></>

  return (
    <>
      <MemberHeader
        user={user}
        currentPath="掲示板"
        displayBreadCrumb={false}
      />
      <ContirbutionContent
        {...props}
        showProfile={(id: number) =>
          window.open(companyDetail(id, false, false), '_blank')
        }
        showMessages={setViewingContributionId}
        userId={user.id}
        isFree={user.plan.freeAndPartner}
        isSalesDemoPlan={user.plan.salesDemo}
        showTipsLink={user.plan.paid || user.plan.isAdminRole}
        currentPage={props.currentPage}
        isPaidForFilter={props.isPaidForFilter}
      />
      {/* FIXME: ProfileViewModal をインポートしないと PremiumContributions.tsx のレイアウトが崩れるため残す */}
      {viewingUserId && (
        <ProfileViewModal
          id={viewingUserId}
          close={() => setViewingUserId(undefined)}
        />
      )}
      {viewingContributionId && (
        <ContributionMessagesModal
          id={viewingContributionId}
          close={() => setViewingContributionId(undefined)}
        />
      )}
    </>
  )
}

type ContributionContentProps = {
  contributions: Contribution[]
  contributionKeywords?: ContributionKeywordsParam[]
  showProfile: (id: number) => void
  showMessages: (id: number) => void
  queryParam?: ContributionsQueryParam
  userId: number
  isFree?: boolean
  isSalesDemoPlan?: boolean
  showTipsLink?: boolean
  currentPage: number
  totalCount: number
  onePageDisplayItems: number
  isMyContributionsPage?: boolean
  contributionSearchConditionsData?: ContributionSearchConditionsData[]
  isPaidForFilter: boolean
  availableCount: number
}
const ContirbutionContent: VFC<ContributionContentProps> = memo(
  ({
    availableCount,
    contributions,
    contributionKeywords = [],
    queryParam,
    showProfile,
    showMessages,
    userId,
    isFree,
    isSalesDemoPlan = false,
    showTipsLink,
    currentPage,
    totalCount,
    onePageDisplayItems,
    isMyContributionsPage = false,
    contributionSearchConditionsData,
    isPaidForFilter,
  }) => {
    const [page, setPage] = useState<number>(currentPage)
    const [searchQueryParam, setSearchQueryParam] = useState<
      ContributionsQueryParam | undefined
    >(queryParam)

    const [searchAvailableCount, setSearchAvailableCount] =
      useState<number>(availableCount)
    const [searchContributions, setSearchContributions] =
      useState<Contribution[]>(contributions)
    const [searchContributionKeywords, setSearchContributionKeywords] =
      useState<ContributionKeywordsParam[]>(contributionKeywords)
    const [searchTotalCount, setSearchTotalCount] = useState<number>(totalCount)
    const [searchOnePageDisplayItems, setSearchOnePageDisplayItems] =
      useState<number>(onePageDisplayItems)
    const [
      searchContributionSearchConditionsData,
      setSearchContributionSearchConditionsData,
    ] = useState<ContributionSearchConditionsData[] | undefined>(
      contributionSearchConditionsData,
    )
    const [selectedSearchCondition, setSelectedSearchCondition] = useState<
      ContributionSearchConditionsData | undefined
    >({
      id: -3,
      companyId: -1,
      title: '全て',
      params: {},
      createdAt: '',
      updatedAt: '',
    })
    const [searchCondition, setSearchCondition] = useState<
      ContributionSearchConditionsData | undefined
    >(undefined)
    const [isOpenFilterCreatePopUpMenu, setIsOpenFilterCreatePopUpMenu] =
      useState(false)
    const [isOpenFilterSettingModal, setIsOpenFilterSettingModal] =
      useState(false)
    const [isOpenFilterDeleteModal, setIsOpenFilterDeleteModal] =
      useState(false)

    const [keywordsSell, setKeywordsSell] = useState<string[]>([])
    const [keywordsBuy, setKeywordsBuy] = useState<string[]>([])
    const firstRendered = useRef(true)
    const [selectedFreeWords, setSelectedFreeWords] = useState<string[]>([])
    const [selectedTypeOfPurchases, setSelectedTypeOfPurchases] = useState<
      string[]
    >([])
    const [selectedSpecialties, setSelectedSpecialties] = useState<number[]>([])
    const [selectedBudgets, setSelectedBudgets] = useState<string[]>([])
    const [selectedIntroductionTimings, setSelectedIntroductionTimings] =
      useState<string[]>([])
    const [showMenu, setShowMenu] = useState<boolean>(false)

    useEffect(() => {
      if (searchQueryParam) {
        if (searchQueryParam.free_word)
          setSelectedFreeWords(searchQueryParam.free_word.split('|'))

        if (searchQueryParam.type_of_purchase)
          setSelectedTypeOfPurchases(
            searchQueryParam.type_of_purchase.split('|'),
          )

        if (searchQueryParam.specialties)
          setSelectedSpecialties(
            searchQueryParam.specialties
              .split('|')
              .map((item: string) => Number(item)),
          )

        if (searchQueryParam.budget)
          setSelectedBudgets(searchQueryParam.budget.split('|'))

        if (searchQueryParam.introduction_timing)
          setSelectedIntroductionTimings(
            searchQueryParam.introduction_timing.split('|'),
          )

        const selectedFreeWordsSet = new Set(
          searchQueryParam?.free_word?.split('|') || [],
        )
        if (searchQueryParam.use_keyword) {
          searchContributionKeywords &&
            searchContributionKeywords.forEach((keyword) => {
              if (keyword.kind === 'sell' && !keyword.isHidden)
                setKeywordsSell((prev) => [
                  ...new Set([...prev, keyword.keyword]),
                ])

              if (keyword.kind === 'buy' && !keyword.isHidden)
                setKeywordsBuy((prev) => [
                  ...new Set([...prev, keyword.keyword]),
                ])

              selectedFreeWordsSet.add(keyword.keyword)
            })

          setSelectedFreeWords([...selectedFreeWordsSet])
        } else {
          setKeywordsBuy([])
          setKeywordsSell([])
        }
      }

      if (firstRendered.current) firstRendered.current = false
    }, [searchQueryParam, searchContributionKeywords])

    const { data: masterData } = useMasterData()
    const masterTypeOfPurchases: MasterData[] = [
      {
        value: 'buy',
        label: '導入したい',
      },
      {
        value: 'consultation',
        label: '相談したい',
      },
      {
        value: 'sell',
        label: 'PRしたい',
      },
    ]
    const onChangeForAll = () => {
      const condition: ContributionSearchConditionsData = {
        id: -3,
        companyId: -1,
        title: '全て',
        params: {},
        createdAt: '',
        updatedAt: '',
      }
      let query: ContributionsQueryParam = {
        type_of_purchase: condition.params?.typeOfPurchase,
        specialties: condition.params?.specialties,
        budget: condition.params?.budget,
        introduction_timing: condition.params?.introductionTiming,
      }
      setSelectedSearchCondition(condition)
      setSelectedFreeWords([])
      setSelectedTypeOfPurchases([])
      setSelectedSpecialties([])
      setSelectedBudgets([])
      setSelectedIntroductionTimings([])
      onClickSearch(query)
    }

    const onChangeFoKeyword = () => {
      const condition: ContributionSearchConditionsData = {
        id: -2,
        companyId: -1,
        title: 'キーワード一致',
        params: {
          freeWord: '',
          typeOfPurchase: '',
          specialties: '',
          budget: '',
          introductionTiming: '',
        },
        createdAt: '',
        updatedAt: '',
      }
      let query: ContributionsQueryParam = {
        use_keyword: true,
      }
      setSelectedSearchCondition(condition)
      setSelectedFreeWords([])
      setSelectedTypeOfPurchases([])
      setSelectedSpecialties([])
      setSelectedBudgets([])
      setSelectedIntroductionTimings([])
      onClickSearch(query)
    }
    const onClickSearch = async (q?: ContributionsQueryParam) => {
      let query: ContributionsQueryParam = {}
      let isCreate = false
      if (q) query = q
      else {
        if (selectedFreeWords.length > 0) {
          query.free_word = selectedFreeWords.join('|')
          isCreate = selectedSearchCondition?.params?.freeWord
            ? selectedSearchCondition?.params.freeWord !== query.free_word
              ? true
              : isCreate
            : true
        } else if (selectedSearchCondition?.params?.freeWord) isCreate = true

        if (selectedTypeOfPurchases.length > 0) {
          query.type_of_purchase = selectedTypeOfPurchases.join('|')
          isCreate = selectedSearchCondition?.params?.typeOfPurchase
            ? selectedSearchCondition?.params.typeOfPurchase !==
              query.type_of_purchase
              ? true
              : isCreate
            : true
        } else if (selectedSearchCondition?.params?.typeOfPurchase)
          isCreate = true

        if (selectedSpecialties.length > 0) {
          query.specialties = selectedSpecialties.join('|')
          isCreate = selectedSearchCondition?.params?.specialties
            ? selectedSearchCondition?.params.specialties !== query.specialties
              ? true
              : isCreate
            : true
        } else if (selectedSearchCondition?.params?.specialties) isCreate = true

        if (selectedBudgets.length > 0) {
          query.budget = selectedBudgets.join('|')
          isCreate = selectedSearchCondition?.params?.budget
            ? selectedSearchCondition?.params.budget !== query.budget
              ? true
              : isCreate
            : true
        } else if (selectedSearchCondition?.params?.budget) isCreate = true

        if (selectedIntroductionTimings.length > 0) {
          query.introduction_timing = selectedIntroductionTimings.join('|')
          isCreate = selectedSearchCondition?.params?.introductionTiming
            ? selectedSearchCondition?.params.introductionTiming !==
              query.introduction_timing
              ? true
              : isCreate
            : true
        } else if (selectedSearchCondition?.params?.introductionTiming)
          isCreate = true
      }
      setSearchQueryParam(query)
      const data = await fetchContributions(query, 1)
      setPage(1)
      setSearchAvailableCount(data.availableCount)
      setSearchContributions(data.contributions)
      setSearchContributionKeywords(data.contributionKeywords)
      setSearchTotalCount(data.totalCount)
      setSearchOnePageDisplayItems(data.onePageDisplayItems)

      if (
        searchContributionSearchConditionsData &&
        searchContributionSearchConditionsData?.length >=
          CONTRIBUTION_SEARCH_CONDITIONS_MAX_COUNT
      )
        isCreate = false

      setIsOpenFilterCreatePopUpMenu(isCreate)
    }
    const onClickFetchContributionSearchConditions = async (
      isCreate: boolean,
    ) => {
      const data = await fetchContributionSearchConditions()
      setSearchContributionSearchConditionsData(data)
      if (!isCreate) {
        const d = data.find((item) => {
          return item.id === selectedSearchCondition?.id
        })
        if (d) setSelectedSearchCondition(d)
        else {
          setSelectedSearchCondition({
            id: -3,
            companyId: -1,
            title: '全て',
            params: {},
            createdAt: '',
            updatedAt: '',
          })
          onChangeForAll()
        }
      } else setSelectedSearchCondition(data[data.length - 1])
    }

    const onClickPagination = async () => {
      let data: ContibutionsData
      if (!isMyContributionsPage)
        data = await fetchContributions(searchQueryParam, page)
      else data = await fetchMyContributions(page)

      setSearchContributions(data.contributions)
      setSearchContributionKeywords(data.contributionKeywords)
      setSearchTotalCount(data.totalCount)
      setSearchOnePageDisplayItems(data.onePageDisplayItems)
    }
    useEffect(() => {
      onClickPagination()
    }, [page])

    const isSearchDetail = () => {
      if (!searchQueryParam) return false
      else if (
        !searchQueryParam.free_word &&
        !searchQueryParam.type_of_purchase &&
        !searchQueryParam.specialties &&
        !searchQueryParam.budget &&
        !searchQueryParam.introduction_timing &&
        selectedSearchCondition?.id === -3
      )
        return false
      return true
    }

    return (
      <Flex>
        <SideIconMenu isSalesDemoPlan={isSalesDemoPlan} />
        <Wrapper>
          <Flex>
            {!isMyContributionsPage && (
              <SearchContent>
                <>
                  {/* NOTE: 有料会員のみ SearchFilter を表示 */}
                  {isPaidForFilter && (
                    <SearchFilter
                      hasPaid={isPaidForFilter}
                      hasKeywords={searchContributionKeywords.length > 0}
                      searchContributionSearchConditionsData={
                        searchContributionSearchConditionsData
                      }
                      onChangeForAll={onChangeForAll}
                      onChangeFoKeyword={onChangeFoKeyword}
                      onClickSearch={onClickSearch}
                      setSelectedFreeWords={setSelectedFreeWords}
                      setSelectedTypeOfPurchases={setSelectedTypeOfPurchases}
                      setSelectedBudgets={setSelectedBudgets}
                      setSelectedIntroductionTimings={
                        setSelectedIntroductionTimings
                      }
                      setSelectedSpecialties={setSelectedSpecialties}
                      selectedSearchCondition={selectedSearchCondition}
                      setSelectedSearchCondition={setSelectedSearchCondition}
                      setSearchCondition={setSearchCondition}
                      setIsOpenFilterSettingModal={setIsOpenFilterSettingModal}
                      setIsOpenFilterDeleteModal={setIsOpenFilterDeleteModal}
                    />
                  )}
                  <AvailableCount>
                    <p>今月の残り投稿可能件数</p>
                    <Count>
                      <span>{searchAvailableCount}</span>件
                    </Count>
                    <p>毎月1日0時にリセット</p>
                  </AvailableCount>
                  {showTipsLink && (
                    <TipsLink to={GUIDE_LINK} color="blue" mt={56} />
                  )}
                </>
              </SearchContent>
            )}
            <Content>
              {!isMyContributionsPage && (
                <SearchFilterTitleFlex>
                  <SearchFilterTitleContent>
                    <SearchFilterTitleHeader>
                      <SearchFilterTitleStyle>
                        <span>絞り込み条件の詳細</span>
                      </SearchFilterTitleStyle>
                      {/* NOTE: 有料会員のみ Filter を表示 */}
                      {isPaidForFilter && (
                        <Filter
                          onClickOpenMenu={undefined}
                          onClickSearch={onClickSearch}
                          selectedFreeWords={selectedFreeWords}
                          setSelectedFreeWords={setSelectedFreeWords}
                          masterTypeOfPurchases={masterTypeOfPurchases}
                          selectedTypeOfPurchases={selectedTypeOfPurchases}
                          setSelectedTypeOfPurchases={
                            setSelectedTypeOfPurchases
                          }
                          budgets={masterData?.budgets}
                          selectedBudgets={selectedBudgets}
                          setSelectedBudgets={setSelectedBudgets}
                          introductionTimings={masterData?.introductionTimings}
                          selectedIntroductionTimings={
                            selectedIntroductionTimings
                          }
                          setSelectedIntroductionTimings={
                            setSelectedIntroductionTimings
                          }
                          specialties={masterData?.specialties}
                          selectedSpecialties={selectedSpecialties}
                          setSelectedSpecialties={setSelectedSpecialties}
                        />
                      )}
                    </SearchFilterTitleHeader>
                    {isSearchDetail() && (
                      <>
                        <Accordion opened={showMenu}>
                          <FilterParamTable>
                            <FilterSettingParams
                              title={'フリーワード'}
                              selectedItems={
                                searchQueryParam?.use_keyword
                                  ? [
                                      ...new Set([
                                        ...keywordsBuy,
                                        ...keywordsSell,
                                      ]),
                                    ]
                                  : searchQueryParam?.free_word
                                  ? searchQueryParam.free_word.split('|')
                                  : []
                              }
                            />
                            <FilterSettingParams
                              title={'タイプ'}
                              selectedItems={
                                searchQueryParam &&
                                searchQueryParam.type_of_purchase
                                  ? searchQueryParam.type_of_purchase.split('|')
                                  : []
                              }
                              masterData={masterTypeOfPurchases}
                            />
                            <FilterSettingParams
                              title={'分野'}
                              selectedItems={
                                searchQueryParam && searchQueryParam.specialties
                                  ? searchQueryParam.specialties.split('|')
                                  : []
                              }
                              masterData={masterData?.specialties}
                            />
                            <FilterSettingParams
                              title={'予算'}
                              selectedItems={
                                searchQueryParam && searchQueryParam.budget
                                  ? searchQueryParam.budget.split('|')
                                  : []
                              }
                              masterData={masterData?.budgets}
                            />
                            <FilterSettingParams
                              title={'導入時期'}
                              selectedItems={
                                searchQueryParam &&
                                searchQueryParam.introduction_timing
                                  ? searchQueryParam.introduction_timing.split(
                                      '|',
                                    )
                                  : []
                              }
                              masterData={masterData?.introductionTimings}
                            />
                          </FilterParamTable>
                        </Accordion>
                        <SearchQueryParamDetailButton
                          onClick={() => setShowMenu(!showMenu)}
                        >
                          {showMenu ? (
                            <>
                              <ExpandLessRounded className="icon" />
                              <span>詳細条件を閉じる</span>
                            </>
                          ) : (
                            <>
                              <ExpandMoreRounded className="icon" />
                              <span>詳細条件を表示する</span>
                            </>
                          )}
                        </SearchQueryParamDetailButton>
                      </>
                    )}
                  </SearchFilterTitleContent>
                </SearchFilterTitleFlex>
              )}
              {searchContributions.length > 0 ? (
                <>
                  <ContributionCards>
                    {searchContributions.map((contribution) => (
                      <ContributionCard
                        key={contribution.id}
                        contribution={contribution}
                        isFree={!!isFree}
                        isMine={contribution.userId === userId}
                        showProfile={showProfile}
                        showMessages={
                          isMyContributionsPage ? showMessages : undefined
                        }
                        isMyContributionsPage={isMyContributionsPage}
                        keywords={{
                          buy: keywordsBuy,
                          sell: keywordsSell,
                        }}
                        searchFreeWords={
                          searchQueryParam && searchQueryParam.free_word
                            ? searchQueryParam.free_word.split('|')
                            : []
                        }
                      />
                    ))}
                  </ContributionCards>
                  <Pagination
                    itemSize={searchTotalCount}
                    setIndex={setPage}
                    forcePage={page - 1}
                    onePageDisplayItems={searchOnePageDisplayItems}
                  ></Pagination>
                </>
              ) : (
                <Empty>該当する投稿はありません</Empty>
              )}
            </Content>
          </Flex>
          {isOpenFilterCreatePopUpMenu && (
            <FilterCreatePopUpMenu
              close={() => {
                setIsOpenFilterCreatePopUpMenu(false)
              }}
              submit={() => {
                setIsOpenFilterCreatePopUpMenu(false)
                setSearchCondition(undefined)
                setIsOpenFilterSettingModal(true)
              }}
            />
          )}
          {isOpenFilterSettingModal && (
            <FilterSettingModal
              selectedFreeWords={selectedFreeWords}
              masterTypeOfPurchases={masterTypeOfPurchases}
              selectedTypeOfPurchases={selectedTypeOfPurchases}
              specialties={masterData?.specialties}
              selectedSpecialties={selectedSpecialties}
              budgets={masterData?.budgets}
              selectedBudgets={selectedBudgets}
              introductionTimings={masterData?.introductionTimings}
              selectedIntroductionTimings={selectedIntroductionTimings}
              close={() => setIsOpenFilterSettingModal(false)}
              searchCondition={searchCondition}
              onClickFetchContributionSearchConditions={
                onClickFetchContributionSearchConditions
              }
            />
          )}
          {isOpenFilterDeleteModal && (
            <FilterDeleteModal
              close={() => setIsOpenFilterDeleteModal(false)}
              searchCondition={searchCondition}
              onClickFetchContributionSearchConditions={
                onClickFetchContributionSearchConditions
              }
            />
          )}
        </Wrapper>
      </Flex>
    )
  },
)

const Content = styled.div`
  background-color: ${_contributionsBackground};
  padding-top: 20px;
  flex: 4;
`

const SearchContent = styled.div`
  padding: 40px 20px 40px 40px;
  background-color: ${_contributionsBackground};
  width: 350px;
`

const SearchFilterTitleFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`

const SearchFilterTitleContent = styled.div`
  background: white;
  border-radius: 5px;
  margin: 20px 0px;
  padding: 24px;
  width: 936px;
  max-width: min(80vw, 936px);
  box-shadow: ${defaultShadow};

  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`

const SearchFilterTitleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  font-weight: ${fontWeightBold};
`

const SearchFilterTitleStyle = styled.div`
  display: flex;
  align-items: center;
  width: 880px;
  max-width: min(70vw, 880px);

  span {
    min-width: 130px;
    color: ${_profilegreen};
  }
  label {
    margin: 0px 8px;
    display: inline-block;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const Flex = styled.div`
  display: flex;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  /* NOTE: PremiumContributions の横幅を制御するため */
  width: calc(100% - ${SIDE_ICON_MENU_WIDTH});
`

const ContributionCards = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`

const Empty = styled.div`
  font-size: 20px;
  font-weight: ${fontWeightBold};
  margin-top: 50px;
  text-align: center;
  color: ${_filterbg};
`

const Accordion = styled.div<{ opened: boolean }>`
  ${({ opened }) =>
    opened
      ? css`
          visibility: visible;
        `
      : css`
          max-height: 0px;
          visibility: hidden;
        `}
`

const FilterParamTable = styled.div`
  display: flex;
  flex-direction: column;
`

const SearchQueryParamDetailButton = styled.button`
  width: 888px;
  max-width: min(80vw, 888px);
  height: 40px;
  margin-top: 20px;
  font-weight: 700;
  font-size: 14px;
  color: ${_profilegreen};
  background: #f3fbfa;
  border-radius: 5px;
  text-align: center;

  .icon {
    transform: translate(0px, -2px);
    margin-right: 10px;
  }
`

const AvailableCount = styled.div`
  background: ${truewhite};
  text-align: center;
  margin-top: 29px;
  border-radius: 5px;
  padding: 5px;
  box-shadow: ${defaultShadow};
  font-weight: ${fontWeightBold};

  p {
    margin: 10px 0;
  }
`
const Count = styled.p`
  font-size: 20px;
  margin: 30px 0;
  span {
    color: ${_contributiontext};
    margin-right: 4px;
  }
`
